'use client';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { orderSearchParams } from '@/utils/search/params';

interface Props {
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const Paginator = ({ totalCount, page, pageSize, totalPages }: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const visiblePages = useMemo(() => {
    const pages = [];
    const showEllipsis = totalPages > 7;

    if (showEllipsis) {
      if (page <= 3) {
        // Show first 4 pages + ellipsis + last page
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        // Show first page + ellipsis + last 4 pages
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        // Show first page + ellipsis + current-1, current, current+1 + ellipsis + last page
        pages.push(1);
        pages.push('...');
        pages.push(page - 1);
        pages.push(page);
        pages.push(page + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    } else {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    }

    return pages;
  }, [page, totalPages]);

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    router.push(`${pathname}?${orderSearchParams(params)}`);
  };

  return (
    <div className="flex justify-center sm:justify-end">
      <div className="flex items-center gap-2">
        {page > 1 && (
          <button
            onClick={() => page > 1 && handlePageChange(page - 1)}
            disabled={page <= 1}
            className="p-1 rounded-md disabled:cursor-not-allowed text-zinc-400 hover:text-white disabled:text-zinc-600"
          >
            <ArrowLeft size={24} />
          </button>
        )}

        {visiblePages.map((pageNum, idx) =>
          pageNum === '...' ? (
            <span key={`ellipsis-${idx}`} className="px-2 py-2 text-zinc-400">
              ...
            </span>
          ) : (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum as number)}
              className={`px-3 py-1 rounded-md ${
                pageNum === page
                  ? 'border-white border text-white'
                  : 'hover:border-white border border-zinc-500 border-opacity-50 text-zinc-400 hover:text-white'
              }`}
            >
              {pageNum}
            </button>
          )
        )}

        {page < totalPages && (
          <button
            onClick={() => page < totalPages && handlePageChange(page + 1)}
            disabled={page >= totalPages}
            className="p-1 rounded-md disabled:cursor-not-allowed text-zinc-400 hover:text-white disabled:text-zinc-600"
          >
            <ArrowRight size={24} />
          </button>
        )}
      </div>
    </div>
  );
};

export default Paginator;
